/**
 * @lodex-pro/web-billing — entry point.
 *
 * Phase 4 / Plan 04-10 — bootstraps Amplify v6 BEFORE the React tree mounts so
 * `cognitoUserPoolsTokenProvider.setKeyValueStorage(...)` is wired before any
 * call to `signIn`, `confirmSignIn`, `fetchAuthSession`, etc.
 *
 * Tenant type literal: `hauler` (web-billing serves hauler billing staff only;
 * legal brand `Paelode Systems` is the surface chrome but the Cognito pool is
 * still the hauler pool — billing is a role inside a hauler tenant).
 *
 * Pools come from `VITE_COGNITO_POOL_CONFIG_JSON` (a JSON array of 4 entries in
 * CDK iteration order). See `packages/web-dispatch/src/main.tsx` for the same
 * pattern. Web-billing duplicates the bootstrap because each Vite app has its
 * own entry point.
 *
 * Spec: CONTEXT D-03, D-05, D-07; RESEARCH § Pattern 4.
 */

// CSS import MUST be first so the Tailwind entry + dark tokens are wired before
// any component imports (mirrors packages/web-dispatch/src/main.tsx).
import './styles.css';

import React from 'react';
import ReactDOM from 'react-dom/client';
import { bootstrapWebAmplify, QueryProvider, ApiOptsProvider } from '@lodex-pro/web-shared';
import type { PoolConfigEntry } from '@lodex-pro/shared/auth/amplify-config';

import { App } from './App';
import { PublicPayPage } from './routes/PublicPayPage';

const POOLS_RAW = (import.meta.env['VITE_COGNITO_POOL_CONFIG_JSON'] as string | undefined) ?? '[]';
const API_BASE_URL =
  (import.meta.env['VITE_API_BASE_URL'] as string | undefined) ?? 'http://localhost:3000';
const pools = JSON.parse(POOLS_RAW) as ReadonlyArray<PoolConfigEntry>;

bootstrapWebAmplify({
  pools,
  tenantType: 'hauler',
  apiBaseUrl: API_BASE_URL,
});

const rootElement = document.getElementById('root');
if (rootElement === null) {
  throw new Error('Root element #root not found in index.html');
}

// OPEN-212 PR C — the pay link in a Paelode invoice email opens /pay/<token>: no sign-in, so
// it is rendered before (and instead of) the app and its auth gate.
const PAY_PAGE = window.location.pathname.startsWith('/pay/');

ReactDOM.createRoot(rootElement).render(
  PAY_PAGE ? (
    <React.StrictMode>
      <PublicPayPage
        apiBaseUrl={API_BASE_URL}
        pathname={window.location.pathname}
        search={window.location.search}
      />
    </React.StrictMode>
  ) : (
    <React.StrictMode>
      <QueryProvider>
        <ApiOptsProvider opts={{ baseUrl: API_BASE_URL }}>
          <App />
        </ApiOptsProvider>
      </QueryProvider>
    </React.StrictMode>
  ),
);
